import classNames from 'classnames';
import { Fragment, memo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FilterAccordion } from '@sorare/core/src/atoms/layout/Accordion';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useLocalStorageToggle } from '@sorare/core/src/hooks/useLocalStorageToggle';
import {
  FilterSeparator,
  FilterWidget,
  FiltersGroup as FiltersGroupType,
} from '@sorare/core/src/lib/filters';

import { ClearAllFilters } from 'search/ClearAllFilters';
import { FiltersGroup } from 'search/FiltersGroup';

type Props = {
  filters: FiltersGroupType[];
  advancedFilters?: FiltersGroupType[];
  skipClearAllButton?: boolean;
};

const Root = styled(Vertical).attrs({ gap: 0 })``;
const FilterRoot = styled.div`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const Separator = styled.div`
  border-top: 1px solid var(--c-nd-50);
`;
const Footer = styled.div`
  &.withBorder {
    border-top: 1px solid var(--c-nd-50);
  }
  padding: var(--double-unit) 0;
`;

const messages = defineMessages({
  showMore: {
    id: 'FiltersManager.showMoreFilters',
    defaultMessage: 'Show more filters',
  },
  showLess: {
    id: 'FiltersManager.showLessFilters',
    defaultMessage: 'Show less filters',
  },
});

const Filter = ({ filter }: { filter: FilterWidget | FilterSeparator }) => {
  if (filter.type === 'separator') {
    return <Separator />;
  }
  if (filter.type === 'raw') {
    return <filter.Component visible />;
  }
  return (
    <FilterRoot key={filter.key}>
      <FilterAccordion
        title={filter.title}
        startsOpen={filter.accordionOptions?.startsOpen}
      >
        {({ expanded }) => filter.Component({ visible: expanded })}
      </FilterAccordion>
    </FilterRoot>
  );
};

export const Filters = ({ filters }: { filters: Props['filters'] }) => {
  return (
    <Vertical gap={2}>
      {filters.map(filtersGroup => {
        if (filtersGroup.filter) {
          return (
            <Fragment key={filtersGroup.slug}>
              {filtersGroup.filter.Component({ visible: true })}
            </Fragment>
          );
        }

        return (
          <FiltersGroup key={filtersGroup.slug} title={filtersGroup.title}>
            {filtersGroup.filters.map((filter, ii) => {
              const key =
                filter.type === 'separator' ? `separator-${ii}` : filter.key;
              return <Filter key={key} filter={filter} />;
            })}
          </FiltersGroup>
        );
      })}
    </Vertical>
  );
};

export const FiltersManager = memo(function FiltersManager({
  filters,
  advancedFilters,
  skipClearAllButton,
}: Props) {
  const [showAdvancedFilters, toggleShowAdvancedFilters] =
    useLocalStorageToggle('SHOW_ADVANCED_FILTERS', false);

  const [lastFilter] = filters.flatMap(filter => filter.filters).slice(-1);
  const flatAdvancedFilters = advancedFilters?.flatMap(
    filter => filter.filters
  );
  const lastAdvancedFilter = flatAdvancedFilters
    ? flatAdvancedFilters.slice(-1)[0]
    : null;

  const lastItemIsSeparator =
    ((!showAdvancedFilters || !flatAdvancedFilters) &&
      lastFilter.type === 'separator') ||
    (showAdvancedFilters &&
      lastAdvancedFilter &&
      lastAdvancedFilter.type === 'separator');

  return (
    <Root>
      <div>
        <Filters filters={filters} />
        {advancedFilters && (
          <Collapse open={showAdvancedFilters} hideGap>
            <Filters filters={advancedFilters} />
          </Collapse>
        )}
      </div>
      {(!!advancedFilters || !skipClearAllButton) && (
        <Footer className={classNames({ withBorder: !lastItemIsSeparator })}>
          {advancedFilters && (
            <button type="button" onClick={toggleShowAdvancedFilters}>
              <BodyM color="var(--c-brand-300)">
                <FormattedMessage
                  {...(showAdvancedFilters
                    ? messages.showLess
                    : messages.showMore)}
                />
              </BodyM>
            </button>
          )}
          {!skipClearAllButton && <ClearAllFilters />}
        </Footer>
      )}
    </Root>
  );
});
